import React, { useState } from "react";
import { Button, Dropdown, DropdownButton, Form } from "react-bootstrap";
import { proxyAPI } from "../proxyAPI";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { ApiDataType } from "../store/slices/settingsSlice";

export default function ApiData({
  jsonText,
  onSave,
  editedField,
  close,
}: {
  close: () => void;
  editedField: ApiDataType;
  jsonText: string;
  onSave: (f: ApiDataType) => void;
}) {
  let parsedJSON;
  try {
    parsedJSON = JSON.parse(editedField.RawJson || jsonText);
  } catch (error) {
    parsedJSON = null;
  }

  const [field, setField] = useState<ApiDataType>(editedField);
  const [testResult, setTestResult] = useState("");
  const [json, setJson] = useState<{}>();

  const handleAdd = async () => {
    const res = await proxyAPI({
      prefix: "logic/workflows/api",
      url: "api-try-extract",
      method: "POST",
      body: {
        ApiData: { ...field, IsArray: field.Path.search("[*]") !== -1 },
        RawJson: json || parsedJSON,
      },
    });
    if (res) {
      setTestResult(res.data);
    }
  };

  const generateHTML = (json: any, prevKey: string = "") => {
    const keys = Object.keys(json);

    const prevKeyLength = prevKey.slice(1).length;
    const wildCardPath =
      field.Path.slice(0, prevKeyLength) +
      ".[*]" +
      field.Path.slice(prevKeyLength + 4);
    return (
      <>
        {Array.isArray(json) ? "[" : "{"}
        {typeof json === "object" && !Array.isArray(json) && prevKey && (
          <>
            <button
              type="button"
              className={`btn btn-link p-0 px-2 m-0 fw-bold  ${
                field?.Path === prevKey.slice(1) + ".*"
                  ? "bg-primary-subtle"
                  : ""
              }`}
              onClick={() =>
                setField({ ...field, Path: prevKey.slice(1) + ".*" })
              }
              disabled={field?.Path === prevKey.slice(1) + ".*"}
            >
              Select raw JSON
            </button>
            <button
              type="button"
              className={`btn btn-link p-0 px-2 m-0 fw-bold  ${
                field?.Path === prevKey.slice(1) + ".*"
                  ? "bg-primary-subtle"
                  : ""
              }`}
              onClick={() => {
                setJson(json);
                setField({ ...field, Path: "" });
              }}
            >
              Use as starting point
            </button>
          </>
        )}
        <div className={`ms-4`}>
          {keys.map((key) => {
            const currentKey =
              prevKey + (Array.isArray(json) ? `.[${key}]` : `.${key}`);

            if (json[key] && typeof json[key] === "object") {
              return (
                <div
                  key={currentKey}
                  className={`p-2 border rounded-2 ${
                    field?.Path === currentKey.slice(1) + ".*"
                      ? "bg-primary-subtle"
                      : ""
                  }`}
                >
                  {key}: {generateHTML(json[key], currentKey)},
                </div>
              );
            }

            return (
              <div
                key={currentKey}
                className={`p-2 border rounded-2 ${
                  field?.Path === currentKey.slice(1) ? "bg-primary-subtle" : ""
                }`}
              >
                <button
                  type="button"
                  className={`btn btn-link p-0 px-2 m-0 fw-bold ${
                    field?.Path === currentKey.slice(1)
                      ? "bg-primary-subtle"
                      : ""
                  }`}
                  title={currentKey}
                  onClick={() =>
                    setField({ ...field, Path: currentKey.slice(1) })
                  }
                  disabled={field?.Path === currentKey.slice(1)}
                >
                  {key}
                </button>
                :{" "}
                {typeof json[key] === "string"
                  ? `"${json[key]}"`
                  : `${json[key]}`}
                ,
              </div>
            );
          })}
        </div>
        {Array.isArray(json) ? "]" : "}"}
      </>
    );
  };

  return (
    <div className="row">
      <div className="col-12 col-lg-6">
        <div className="mb-2">
          <div>Data Label</div>

          <Form.Control
            value={field.Name}
            onChange={(e) => setField({ ...field, Name: e.target.value })}
          />
        </div>
        <div className="mb-2">
          <div>Default Value</div>
          <Form.Control
            value={field.DefaultValue}
            onChange={(e) =>
              setField({ ...field, DefaultValue: e.target.value })
            }
          />
        </div>
        <div className="mb-2">
          <div>Selected Path</div>
          <div className="p-3 bg-white rounded-3 border d-flex align-items-center">
            {(() => {
              let currentJSON = parsedJSON;
              return (
                <>
                  {field.Path.split(".").map((p, i, paths) => {
                    currentJSON = Array.isArray(currentJSON)
                      ? currentJSON[0]
                      : currentJSON?.[p.replace(/\[|\]/g, "")];

                    const t =
                      p === "*"
                        ? "Raw object"
                        : Array.isArray(currentJSON)
                        ? "Array"
                        : typeof currentJSON;

                    if (p.search(/\[.*\]/) !== -1) {
                      const pathDropdownOptions = [
                        {
                          value: "[0]",
                          label: (
                            <span>
                              First{" "}
                              <span className="badge bg-secondary">{t}</span>
                            </span>
                          ),
                        },
                        {
                          value: "[last]",
                          label: (
                            <span>
                              Last{" "}
                              <span className="badge bg-secondary">{t}</span>
                            </span>
                          ),
                        },
                        {
                          value: "[*]",
                          label: (
                            <span>
                              All{" "}
                              <span className="badge bg-secondary">{t}s</span>
                            </span>
                          ),
                        },
                      ];

                      return (
                        <React.Fragment key={i}>
                          {!!i && (
                            <FontAwesomeIcon
                              icon={faChevronRight}
                              className="mx-2"
                            />
                          )}

                          <DropdownButton
                            id={`dropdown-button-drop-${i}`}
                            title={
                              pathDropdownOptions.find(
                                (option) => option.value === p
                              )?.label || (
                                <span>
                                  <span className="badge bg-secondary mx-1">
                                    {t}
                                  </span>
                                  #{parseInt(p.replace(/\[|\]/g, ""))}
                                </span>
                              )
                            }
                            variant=""
                            className="fw-bold"
                          >
                            {pathDropdownOptions.map((option) => (
                              <Dropdown.Item
                                onClick={() =>
                                  setField({
                                    ...field,
                                    Path: [
                                      ...paths.slice(0, i),
                                      option.value,
                                      ...paths.slice(i + 1),
                                    ].join("."),
                                  })
                                }
                              >
                                {option.label}
                              </Dropdown.Item>
                            ))}
                            <Dropdown.Divider />
                            <Dropdown.Item
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              Enter index:
                              <Form.Control
                                type="number"
                                value={p.replace(/\[|\]/g, "")}
                                onChange={(e) =>
                                  setField({
                                    ...field,
                                    Path: [
                                      ...paths.slice(0, i),
                                      "[" +
                                        (parseInt(e.target.value) || "0") +
                                        "]",
                                      ...paths.slice(i + 1),
                                    ].join("."),
                                  })
                                }
                              />
                            </Dropdown.Item>
                          </DropdownButton>
                        </React.Fragment>
                      );
                    }
                    return (
                      <React.Fragment key={i}>
                        {!!i && (
                          <FontAwesomeIcon
                            icon={faChevronRight}
                            className="mx-2"
                          />
                        )}
                        {p}{" "}
                        {p && (
                          <span className="badge bg-secondary mx-1">{t}</span>
                        )}
                      </React.Fragment>
                    );
                  })}
                </>
              );
            })()}
          </div>
        </div>

        <div className="mb-2 text-end">
          <Button
            variant="secondary"
            disabled={!field.Path}
            onClick={() => handleAdd()}
            title={JSON.stringify(field.Path)}
          >
            Test
          </Button>
          <Button
            variant="secondary"
            className="ms-2"
            onClick={() =>
              onSave({
                ...field,
                IsArray: field.Path.search("[*]") !== -1,
                RawJson: JSON.stringify(json || parsedJSON),
              })
            }
          >
            Save Data Field
          </Button>
          <Button variant="secondary" className="ms-2" onClick={close}>
            Cancel
          </Button>
        </div>
        {testResult && (
          <>
            <div className="mb-2 bg-white p-3 rounded-3 border">
              Your test returned:{" "}
              <pre>{JSON.stringify(testResult, null, 2)}</pre>
            </div>
            <div className="mb-2 text-end"></div>
          </>
        )}
      </div>
      <div className="col-12 col-lg-6">
        <div className="d-flex justify-content-between">
          Parsed JSON
          <button
            type="button"
            className={`btn btn-link p-0 px-2 m-0 fw-bold`}
            onClick={() => {
              try {
                setJson(JSON.parse(jsonText));
                setField({ ...field, Path: "" });
              } catch (error) {
                toast.error("Invalid JSON");
              }
            }}
          >
            <FontAwesomeIcon icon={faRefresh} />
          </button>
        </div>
        <div
          className="overflow-auto bg-white rounded-3 p-3"
          style={{ maxHeight: 600 }}
        >
          {parsedJSON ? (
            generateHTML(json || parsedJSON)
          ) : (
            <div className="text-danger ">
              Invalid JSON. Please type or paste a valid JSON in the JSON area.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
